'use client'

import { createClient } from '@/core/supabase/client'

type FileItem = {
  url: string
  alt: string
}

export const getFilesUrls = async (paths: string[]): Promise<FileItem[]> => {
  if (!paths || !Array.isArray(paths) || paths.length === 0) {
    return []
  }

  const validPaths = paths
    .filter((path): path is string => typeof path === 'string' && path.trim().length > 0)
    .map((path) => {
      const normalized = path.trim().replace(/^\/+/, '')
      if (normalized.startsWith('app/')) {
        return normalized.replace(/^app\//, '')
      }
      return normalized
    })
    .filter((path) => path.length > 0)

  if (validPaths.length === 0) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[getFilesUrls] Nenhum path válido após normalização. Paths originais:', paths)
    }
    return []
  }

  if (process.env.NODE_ENV === 'development') {
    console.log('[getFilesUrls] Paths normalizados:', validPaths)
  }

  // Usar API server-side com service role (não depende de sessão do usuário)
  try {
    const res = await fetch('/api/storage/signed-urls', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ paths: validPaths }),
    })

    const json = await res.json()
    if (json.ok && Array.isArray(json.urls)) {
      return json.urls
    }

    if (process.env.NODE_ENV === 'development') {
      console.warn('[getFilesUrls] API retornou erro:', json.error)
    }
  } catch (err) {
    console.error('[getFilesUrls] Erro ao chamar API:', err)
  }

  // Fallback: tentar client com sessão e URLs públicas
  return getFilesUrlsFallback(validPaths)
}

async function getFilesUrlsFallback(paths: string[]): Promise<FileItem[]> {
  const client = createClient()
  const expiresIn = 3600 * 6

  try {
    const { data: sessionData, error: sessionError } = await client.auth.getSession()
    if (!sessionError && sessionData?.session) {
      const { data, error } = await client.storage.from('app').createSignedUrls(paths, expiresIn)
      if (!error && data) {
        const output = data
          .map((item, idx) => (item?.signedUrl ? { url: item.signedUrl, alt: `item ${idx}` } : null))
          .filter((item): item is FileItem => item !== null)
        if (output.length > 0) return output
      }
    }
  } catch {
    // ignorar
  }

  return getPublicUrls(paths, client)
}

async function getPublicUrls(
  paths: string[],
  client: ReturnType<typeof createClient>,
): Promise<FileItem[]> {
  const output: FileItem[] = []
  for (let idx = 0; idx < paths.length; idx++) {
    const path = paths[idx]
    const normalizedPath = path.trim().replace(/^\/+/, '').replace(/^app\//, '')
    if (!normalizedPath) continue
    const { data } = client.storage.from('app').getPublicUrl(normalizedPath)
    if (data?.publicUrl) {
      output.push({ url: data.publicUrl, alt: `item ${idx}` })
    }
  }
  return output
}
